/*
  Product badges on product cards and product page images. Loaded by snippets/oumnh-global.liquid.

  One badge is shown per product, in this order:
  1. Sold out (all variants unavailable)
  2. Sale (compare at price above price)
  3. The first "badge" metaobject entry whose tag the product has (case-insensitive)

  Cards: Dawn's card markup cannot be extended without editing core files, so each card's
  product data is fetched from /products/<handle>.js and the badge added to the card image.
  Dawn's own card badges are hidden by oumnh-custom.css, and shown again if the fetch fails.

  Product page: data comes from snippets/oumnh-product-badge.liquid.
*/
(() => {
  const configElement = document.getElementById('oumnh-badge-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const root = (config.root || '/').replace(/\/?$/, '/');
  const requests = new Map();

  const pickBadge = (data) => {
    if (!data.available) return { label: config.soldOut.label, className: `color-${config.soldOut.scheme}` };
    if (data.compare_at_price > data.price) return { label: config.sale.label, className: `color-${config.sale.scheme}` };

    const tags = (data.tags || []).map((tag) => String(tag).trim().toLowerCase());
    const match = (config.badges || []).find((badge) => badge.tag && tags.includes(badge.tag));
    if (!match) return null;
    return { label: match.label, className: 'oumnh-badge--custom', text: match.text, background: match.background };
  };

  const buildBadges = (data) => {
    const badge = pickBadge(data);
    if (!badge || !badge.label) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'oumnh-badges';
    const span = document.createElement('span');
    span.className = `badge oumnh-badge ${badge.className}`;
    span.textContent = badge.label;
    if (badge.text) span.style.setProperty('--oumnh-badge-text', badge.text);
    if (badge.background) span.style.setProperty('--oumnh-badge-bg', badge.background);
    wrapper.appendChild(span);
    return wrapper;
  };

  const fetchProduct = (handle) => {
    if (!requests.has(handle)) {
      requests.set(
        handle,
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Badge data request failed: ${response.status}`);
            return response.json();
          })
          .then((product) => ({
            available: product.available,
            price: product.price,
            compare_at_price: product.compare_at_price || 0,
            tags: product.tags,
          }))
      );
    }
    return requests.get(handle);
  };

  const handleFromCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const path = new URL(link.href, window.location.origin).pathname;
    return decodeURIComponent(path.split('/products/')[1].split('/')[0]);
  };

  const decorateCards = () => {
    document.querySelectorAll('.product-card-wrapper:not([data-oumnh-badges])').forEach((card) => {
      card.dataset.oumnhBadges = 'pending';
      const handle = handleFromCard(card);
      const target = card.querySelector('.card__inner');
      if (!handle || !target) {
        card.classList.add('oumnh-badges--failed');
        return;
      }

      fetchProduct(handle)
        .then((data) => {
          const badges = buildBadges(data);
          if (badges) target.appendChild(badges);
          card.dataset.oumnhBadges = 'done';
        })
        .catch(() => card.classList.add('oumnh-badges--failed'));
    });
  };

  const decorateProductPages = () => {
    document.querySelectorAll('script[data-oumnh-product-badge]').forEach((dataElement) => {
      const scope = dataElement.closest('.shopify-section, .quick-add-modal__content') || document;
      const containers = scope.querySelectorAll('.product__media-list .product-media-container');
      if (!containers.length) return;

      let data;
      try {
        data = JSON.parse(dataElement.textContent);
      } catch (error) {
        return;
      }

      containers.forEach((container) => {
        if (container.querySelector(':scope > .oumnh-badges')) return;
        const badges = buildBadges(data);
        if (badges) container.appendChild(badges);
      });
    });
  };

  let queued = false;
  const decorate = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      decorateCards();
      decorateProductPages();
    });
  };

  decorate();
  // Cards and product media are re-rendered by filtering, variant changes, quick add and recommendations.
  new MutationObserver(decorate).observe(document.body, { childList: true, subtree: true });
})();
